import type { PhoneState } from '@/types/game'

export type Screen =
    | 'survey'
    | 'lobby'
    | 'wait'
    | 'bet'
    | 'hold-assess'
    | 'assess'
    | 'hold-reveal'
    | 'reveal'
    | 'balance'
    | 'facts'
    | 'done'

/** Der eigene Stand, abgeleitet aus dem Serverzustand. */
export interface OwnProgress {
    surveyDone: boolean
    betsDone: boolean
    assessmentDone: boolean
}

/**
 * Was hat dieses Handy schon abgegeben?
 *
 * Bewusst aus dem Serverzustand abgeleitet und nicht lokal mitgeschrieben: Ein
 * neu geladenes Handy hätte sonst keinen eigenen Stand mehr und stünde wieder
 * am Anfang, obwohl die Tipps längst beim Server liegen.
 */
export function ownProgress(state: PhoneState): OwnProgress {
    return {
        surveyDone: state.participant.betExperience !== null,
        // Abgegeben heißt seit WETTBANDIT-67: Der Wettschein wurde bewusst
        // abgeschickt. Vorher sprang das Handy weiter, sobald zu jedem Spiel
        // eine Entscheidung vorlag - eine Rückfrage war damit unmöglich.
        betsDone: state.participant.betsSubmitted,
        assessmentDone: state.participant.guess !== null,
    }
}

/**
 * Welcher Screen gehört zu diesem Zustand?
 *
 * Eine reine Funktion aus Serverzustand und eigenem Stand - der Bildschirm ist
 * damit immer eine Ableitung, nie ein eigener Zustand. Das ist der Kern der
 * Anforderung: Schaltet die Lehrkraft weiter, ändert sich der Serverzustand und
 * das Handy folgt zwangsläufig. Es gibt keinen zweiten Ort, an dem eine
 * abweichende Meinung entstehen könnte.
 */
export function deriveScreen(state: PhoneState, progress = ownProgress(state)): Screen {
    switch (state.session.phase) {
        case 'lobby':
        case 'survey_wait':
            return progress.surveyDone ? 'lobby' : 'survey'

        case 'lesson':
            return 'wait'

        case 'betting':
            return progress.betsDone ? 'hold-assess' : 'bet'

        case 'assessment':
            return progress.assessmentDone ? 'hold-reveal' : 'assess'

        case 'reveal':
            return 'reveal'

        case 'result':
            // Die letzte Auswertungsstufe öffnet auf den Handys Fakten & Hilfe -
            // der eigentliche Zweck der Stunde.
            return state.session.resultStage >= LAST_RESULT_STAGE ? 'facts' : 'balance'

        case 'finished':
            return 'done'
    }
}

/** Die fünfte Stufe (Index 4) ist der Abschluss. */
export const LAST_RESULT_STAGE = 4
